import { useState, useEffect, useRef } from "react";
import "./StickyTitle.css";

const StickyTitle = ({ title }) => {
	const titleRef = useRef(null);
	const [showStickyTitle, setShowStickyTitle] = useState(false);
	const visibilityRef = useRef(false);
	const timeoutRef = useRef(null);
	const observerRef = useRef(null);

	useEffect(() => {
		const titleElement = titleRef.current;

		const setupObserver = () => {
			const safeAreaInsetTop = Math.max(
				parseFloat(
					getComputedStyle(document.documentElement)
						.getPropertyValue("--safe-area-inset-top")
						.replace("px", "")
				) || 0,
				0
			);

			// Cleanup previous observer if it exists
			if (observerRef.current && titleElement) {
				observerRef.current.unobserve(titleElement);
			}

			observerRef.current = new IntersectionObserver(
				([entry]) => {
					const shouldBeVisible = !entry.isIntersecting;

					// Only act on state change
					if (shouldBeVisible !== visibilityRef.current) {
						// Clear previous timeout if any
						if (timeoutRef.current) {
							clearTimeout(timeoutRef.current);
							timeoutRef.current = null;
						}

						// Debounce very short flickering transitions (bounce)
						timeoutRef.current = setTimeout(() => {
							visibilityRef.current = shouldBeVisible;
							setShowStickyTitle(shouldBeVisible);
							timeoutRef.current = null;
						}, 50);
					}
				},
				{
					root: null,
					threshold: 0,
					rootMargin: `-${45 + safeAreaInsetTop}px 0px 0px 0px`,
				}
			);

			if (titleElement) {
				observerRef.current.observe(titleElement);
			}
		};

		// Initial setup
		setupObserver();

		// Re-run observer on resize / orientation change
		window.addEventListener("resize", setupObserver);
		window.addEventListener("orientationchange", setupObserver);

		// Cleanup on unmount
		return () => {
			if (observerRef.current && titleElement) {
				observerRef.current.unobserve(titleElement);
			}
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			window.removeEventListener("resize", setupObserver);
			window.removeEventListener("orientationchange", setupObserver);
		};
	}, []);

	return (
		<>
			{/* Fixed sticky title shown when main title is out of view */}
			<div className={`sticky-title ${showStickyTitle ? "visible" : ""}`}>
				<span className="sticky-title-text">{title}</span>
			</div>

			<h1
				ref={titleRef}
				className={`page-title ${showStickyTitle ? "visible" : ""}`}
			>
				{title}
			</h1>
		</>
	);
};

export default StickyTitle;
